import React from 'react';
import useStore, { updateDeviceActivities, updateSelectedRecipient } from '../../Store/Store';
import { Avatar, Box } from '@mui/material';
import SearchableSelect from '../../components/SearchableSelect';
import boyAvatar from '../../images/boy.png';
import girlAvatar from '../../images/girl.png';

const RecipientsSelector = ({ fullWidth = false }) => {
  const careRecipients = useStore((state) => state.careRecipients);
  const selectedRecipient = useStore((state) => state.selectedRecipient);
  const width = useStore((state) => state.screenWidth);

  const recipientsArray = Object.values(careRecipients);
  const shouldShowDropdown = recipientsArray.length > 5;

  const handleSelector = (item) => {
    if (selectedRecipient !== item?._id) {
      updateSelectedRecipient(item?._id);
      updateDeviceActivities(null);
    } else {
      updateSelectedRecipient('');
      updateDeviceActivities(null);
    }
  };

  const handleDropdownChange = (event) => {
    const selectedId = event.target.value;

    // Clear selection case
    if (!selectedId) {
      updateSelectedRecipient('');
      updateDeviceActivities(null);
      return;
    }

    // Handle "My self"
    if (selectedId === 'myself') {
      updateSelectedRecipient('myself');
      updateDeviceActivities(null);
      return;
    }

    // Handle regular recipient
    const recipient = recipientsArray.find((r) => r._id === selectedId);
    if (recipient) {
      if (selectedRecipient !== recipient._id) {
        updateSelectedRecipient(recipient._id);
        updateDeviceActivities(null);
      }
    }
  };

  const renderRecipientOption = (recipient) => {
    const getDefaultAvatar = () => {
      if (recipient?.userInfo?.gender === 'female') {
        return girlAvatar;
      } else if (recipient?.userInfo?.gender === 'male') {
        return boyAvatar;
      }
      return boyAvatar; // default fallback
    };

    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, width: '100%' }}>
        <Avatar
          alt={recipient?.fullName}
          src={recipient?.imageUrl || getDefaultAvatar()}
          sx={{ width: 32, height: 32, flexShrink: 0 }}
        />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: '14px',
              fontWeight: 500,
              color: '#333',
              lineHeight: '1.2',
              marginBottom: '2px',
            }}
          >
            {recipient?.userInfo?.firstName + ' ' + recipient?.userInfo?.lastName}
          </div>
          <div
            style={{
              fontSize: '12px',
              color: '#666',
              lineHeight: '1.2',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {recipient?.email}
          </div>
        </Box>
      </Box>
    );
  };

  // Show dropdown version when fullWidth is true
  if (fullWidth) {
    return (
      <Box
        sx={{
          paddingInline: '0px',
          width: '100%',
          marginBlock: '0px',
          marginTop: '0px',
        }}
      >
        <SearchableSelect
          label="Caring for"
          options={recipientsArray}
          value={selectedRecipient}
          onChange={handleDropdownChange}
          renderOption={renderRecipientOption}
          getOptionValue={(recipient) => (recipient.isMyself ? 'myself' : recipient._id)}
          getOptionLabel={(recipient) =>
            `${recipient?.userInfo?.firstName || ''} ${recipient?.userInfo?.lastName || ''}`.trim() || recipient?.email
          }
          searchFields={['userInfo.firstName', 'userInfo.lastName', 'email']}
          placeholder={recipientsArray.length === 0 ? 'No recipients available' : 'Search recipients...'}
          style={{ marginBottom: 0 }}
        />
      </Box>
    );
  }

  // Show avatar version when fullWidth is false

  return (
    <div
      style={{
        paddingInline: '5px',
        display: 'flex',
        width: '96%',
        marginBlock: '-10px',
        justifyContent: 'end',
        marginTop: width > 550 ? '0px' : '15px',
      }}
    >
      {recipientsArray.length === 0 ? (
        <div
          style={{
            padding: '10px',
            color: '#666',
            fontSize: '14px',
            fontStyle: 'italic',
          }}
        >
          {/* No care recipients available */}
        </div>
      ) : (
        recipientsArray.map((item, idx) => {
          const getDefaultAvatar = () => {
            if (item?.userInfo?.gender === 'female') {
              return girlAvatar;
            } else if (item?.userInfo?.gender === 'male') {
              return boyAvatar;
            }
            return boyAvatar; // default fallback
          };

          return (
            <Avatar
              key={idx}
              alt={item?.fullName}
              src={item?.imageUrl || getDefaultAvatar()}
              style={{
                border: selectedRecipient !== item?._id ? '0.2px solid grey' : '3px solid #26786A',
                width: '50px',
                height: '50px',
                cursor: 'pointer',
                marginLeft: '8px',
              }}
              title={item?.email}
              onClick={() => handleSelector(item)}
            />
          );
        })
      )}
    </div>
  );
};

export default RecipientsSelector;
